import type { FormEvent } from 'react'
import {Check, Copy, Minus, Plus} from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

const GOOGLE_SHEET_WEB_APP_URL =
  'https://script.google.com/macros/s/AKfycbx6Hc9Hsz3K-Q097UghmgGXUvmyVfpVnRORsDHaBEHRl1JqX5TmW0FmhA4xg2vIBQbXtQ/exec'

const productOptions = [
  { id: 'set', label: 'The Taste Of...香氛組合', desc: '一次收藏兩種氣味，感受愛情的兩種面貌', price: 1880, unit: '組' },
  { id: 'single', label: '單支香氛', desc: '可依喜好單支選購', price: 980, unit: '支' },
] as const

const scentOptions = [
  { id: 'falling', label: 'Falling in Love' },
  { id: 'never', label: 'Never Cared' },
] as const

type ProductId = typeof productOptions[number]['id']
type ScentId = typeof scentOptions[number]['id']

interface OrderForm {
  name: string
  phone: string
  email: string
  storeName: string
  storeAddress: string
  remitDate: string
  remitLast5: string
  note: string
}

const BANK_FIELDS = [
  { label: '銀行代碼', value: '822（中國信託商業銀行）' },
  { label: '戶名', value: '零加壹文創整合有限公司' },
  { label: '帳號', value: '303540352643' },
] as const

function OrderSection() {
  const [productId, setProductId] = useState<ProductId>('set')
  const [scentId, setScentId] = useState<ScentId>('falling')
  const [quantity, setQuantity] = useState(1)
  const [form, setForm] = useState<OrderForm>({ name: '', phone: '', email: '', storeName: '', storeAddress: '', remitDate: '', remitLast5: '', note: '' })
  const [submitting, setSubmitting] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  const selectedProduct = productOptions.find(p => p.id === productId) ?? productOptions[0]
  const total = quantity * (selectedProduct?.price || 0)

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.min(5, Math.max(1, prev + delta)))
  }

  const handleChange = (field: keyof OrderForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCopy = async (label: string, value: string) => {
    try {
      if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') {
        toast.error('複製失敗，請手動抄錄匯款資訊')
        return
      }
      await navigator.clipboard.writeText(value)
      setCopiedField(label)
      toast.success(`${label}已複製`)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      toast.error('複製失敗，請手動抄錄匯款資訊')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.storeName.trim() || !form.storeAddress.trim()) {
      toast.error('請完整填寫姓名、電話、Email 與 7-ELEVEN 門市資訊')
      return
    }

    if (!form.remitDate.trim() || !form.remitLast5.trim()) {
      toast.error('請填寫匯款日期與帳號末五碼，以利核對款項')
      return
    }

    setSubmitting(true)

    const planLabel =
      productId === 'single'
        ? `${selectedProduct.label} · ${scentId === 'never' ? 'Never Cared' : 'Falling in Love'}`
        : selectedProduct.label

    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      storeName: form.storeName.trim(),
      storeAddress: form.storeAddress.trim(),
      transferDate: form.remitDate.trim(),
      lastFiveDigits: form.remitLast5.trim(),
      plan: planLabel,
      quantity: String(quantity),
      amount: String(total),
      note: form.note.trim(),
    }

    const body = new URLSearchParams(payload)

    try {
      console.log('預購表單開始送出', payload)

      const response = await fetch(GOOGLE_SHEET_WEB_APP_URL, {
        method: 'POST',
        mode: 'no-cors',
        body,
      })

      console.log('預購表單已送出（no-cors 無法讀取詳細回應）', response)
    } catch (error) {
      console.error('預購表單送出失敗，後台錯誤訊息:', error)
    } finally {
      setShowSuccessModal(true)
      setForm({ name: '', phone: '', email: '', storeName: '', storeAddress: '', remitDate: '', remitLast5: '', note: '' })
      setQuantity(1)
      setSubmitting(false)
    }
  }

  return (
    <section id="order" className="bg-zinc-950 py-10 sm:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-2xl mb-8 sm:mb-14">
          <p className="text-xs tracking-[0.3em] text-slate-400 mb-3 sm:mb-4">ORDER NOW</p>
          <h2 className="font-serif text-[21px] sm:text-3xl lg:text-4xl text-stone-50 mb-3 sm:mb-4">立即預購，搶先收藏</h2>
          <p className="text-stone-400 text-sm lg:text-base leading-[1.6]">
            限量發行，售完為止。請先完成匯款，再填寫以下資訊，完成《The Taste Of...》香氛預購。
          </p>
        </div>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 sm:gap-10 lg:gap-16">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 sm:p-8 lg:p-10 h-fit">
            <p className="text-xs tracking-wide text-slate-400 mb-3 sm:mb-4">選擇購買方案</p>

            <div className="grid grid-cols-1 gap-3 mb-5 sm:mb-6">
              {productOptions.map(option => {
                const active = option.id === productId
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setProductId(option.id)}
                    className={`text-left p-3.5 sm:p-4 rounded-sm border transition-colors min-h-12 ${active ? 'border-rose-300 bg-zinc-800' : 'border-zinc-700 hover:border-zinc-500'}`}
                  >
                    <p className="text-sm text-stone-50 mb-1">{option.label}</p>
                    <p className="text-xs text-stone-500 mb-2 sm:mb-3">{option.desc}</p>
                    <p className="font-serif text-lg text-rose-300">
                      NT$
                      {' '}
                      {option.price.toLocaleString()}
                      <span className="text-xs text-stone-500">
                        {' '}
                        /
                        {option.unit}
                      </span>
                    </p>
                  </button>
                )
              })}
            </div>

            {productId === 'single'
              ? (
                  <div className="mb-5 sm:mb-6">
                    <p className="text-xs tracking-wide text-slate-400 mb-2.5 sm:mb-3">選擇香氛</p>
                    <div className="flex gap-3">
                      {scentOptions.map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setScentId(option.id)}
                          className={`flex-1 px-4 py-2.5 text-sm rounded-sm border transition-colors ${scentId === option.id ? 'border-rose-300 text-stone-50 bg-zinc-800' : 'border-zinc-700 text-stone-400 hover:border-zinc-500'}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              : null}

            <div className="flex items-center gap-4 mb-6 sm:mb-8">
              <span className="text-sm text-stone-400">數量</span>
              <div className="flex items-center border border-zinc-700 rounded-sm">
                <button
                  type="button"
                  onClick={() => handleQuantityChange(-1)}
                  className="p-3 min-h-12 min-w-12 flex items-center justify-center text-stone-300 hover:text-stone-50 transition-colors"
                  aria-label="減少數量"
                >
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center text-stone-50 text-sm">{quantity}</span>
                <button
                  type="button"
                  onClick={() => handleQuantityChange(1)}
                  className="p-3 min-h-12 min-w-12 flex items-center justify-center text-stone-300 hover:text-stone-50 transition-colors"
                  aria-label="增加數量"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-zinc-800 pt-5 sm:pt-6 mb-6 sm:mb-8">
              <span className="text-sm text-stone-400">預購總金額</span>
              <span className="font-serif text-2xl text-rose-300">
                NT$
                {' '}
                {total.toLocaleString()}
              </span>
            </div>

            <div className="bg-zinc-800/60 border border-zinc-700 rounded-sm p-4 sm:p-5">
              <p className="text-xs tracking-wide text-slate-400 mb-2.5 sm:mb-3">匯款資訊</p>
              <div className="space-y-2">
                {BANK_FIELDS.map(field => (
                  <div key={field.label} className="flex flex-row items-center justify-between gap-2 text-sm text-stone-300" style={{ whiteSpace: 'nowrap' }}>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1 text-xs sm:text-sm">
                      {field.label}
                      ：
                      {field.value}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(field.label, field.value)}
                      className="inline-flex items-center gap-1 text-xs text-stone-300 hover:text-stone-50 border border-zinc-600 hover:border-zinc-400 rounded-sm px-2 sm:px-3 min-h-9 sm:min-h-11 py-1 transition-colors shrink-0 whitespace-nowrap"
                    >
                      {copiedField === field.label ? <Check size={12} /> : <Copy size={12} />}
                      {copiedField === field.label ? '已複製' : '複製'}
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-stone-500 mt-3 leading-[1.6]">
                售價已含運費，匯款金額請依訂購組數計算。請先完成匯款後再填寫以下資料。
                <br />
                ⚠️ 溫馨提示：轉帳時請勿填寫「備註」，以利專人為您快速核對帳款與安排出貨。
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-sm p-5 sm:p-8 lg:p-10 space-y-4 sm:space-y-5">
            <div>
              <label htmlFor="name" className="block text-xs text-zinc-500 mb-2 tracking-wide">姓名</label>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="請輸入收件人姓名"
                className="w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs text-zinc-500 mb-2 tracking-wide">聯絡電話</label>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="請輸入聯絡電話"
                className="w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs text-zinc-500 mb-2 tracking-wide">Email</label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="請輸入 Email 以接收訂單通知"
                className="w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
              />
            </div>

            <div>
              <a
                href="https://emap.pcsc.com.tw/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center text-xs text-zinc-700 underline underline-offset-2 hover:text-zinc-950 mb-3 transition-colors min-h-8"
              >
                點此查詢 7-11 門市名稱與地址
              </a>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="storeName" className="block text-xs text-zinc-500 mb-2 tracking-wide">7-ELEVEN 門市名稱</label>
                  <input
                    id="storeName"
                    type="text"
                    value={form.storeName}
                    onChange={e => handleChange('storeName', e.target.value)}
                    placeholder="例：中山門市"
                    className="w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="storeAddress" className="block text-xs text-zinc-500 mb-2 tracking-wide">7-ELEVEN 門市地址</label>
                  <input
                    id="storeAddress"
                    type="text"
                    value={form.storeAddress}
                    onChange={e => handleChange('storeAddress', e.target.value)}
                    placeholder="請輸入門市完整地址"
                    className="w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="remitDate" className="block text-xs text-zinc-500 mb-2 tracking-wide">匯款日期</label>
                <input
                  id="remitDate"
                  type="date"
                  value={form.remitDate}
                  onChange={e => handleChange('remitDate', e.target.value)}
                  className="date-input w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="remitLast5" className="block text-xs text-zinc-500 mb-2 tracking-wide">帳號末五碼</label>
                <input
                  id="remitLast5"
                  type="text"
                  maxLength={5}
                  value={form.remitLast5}
                  onChange={e => handleChange('remitLast5', e.target.value)}
                  placeholder="12345"
                  className="w-full px-4 py-3 h-12 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="note" className="block text-xs text-zinc-500 mb-2 tracking-wide">其他備註（選填）</label>
              <textarea
                id="note"
                rows={3}
                value={form.note}
                onChange={e => handleChange('note', e.target.value)}
                placeholder="如有其他需求或留言，請於此填寫"
                className="w-full px-4 py-3 border border-zinc-300 rounded-sm text-sm text-zinc-900 focus:outline-none focus:border-zinc-900 transition-colors resize-none"
              />
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                訂單送出後，將寄送 Email 通知，確認我們已收到您的訂單資訊。完成付款確認後，將依訂單順序安排出貨。
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 h-12 bg-zinc-950 text-stone-50 text-sm tracking-wide hover:bg-zinc-800 active:scale-[0.98] transition-all rounded-sm disabled:opacity-60"
            >
              {submitting ? '處理中...' : `確認預購 NT$ ${total.toLocaleString()}`}
            </button>
          </form>
        </div>
      </div>

      {showSuccessModal
        ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
              <div className="bg-white rounded-sm max-w-sm w-full p-7 sm:p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                  <Check size={24} className="text-emerald-600" />
                </div>
                <h3 className="font-serif text-xl text-zinc-900 mb-3">預購成功</h3>
                <p className="text-sm text-zinc-500 leading-[1.7] mb-6">
                  感謝您的預購！我們已收到您的訂單資訊，專人對帳後將寄發 Email 通知。
                </p>
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full py-3 h-12 bg-zinc-950 text-stone-50 text-sm tracking-wide hover:bg-zinc-800 active:scale-[0.98] transition-all rounded-sm"
                >
                  我知道了
                </button>
              </div>
            </div>
          )
        : null}
    </section>
  )
}

export default OrderSection
